import { useEffect, useMemo, useRef, useState } from 'react';

import useTranslations from '@/i18n/useTranslations';
import { getPreferredLocale } from '@/services/locale.service';

import './styles/PublicationSchedulePicker.css';

function formatDateInput(isoDate) {
  if (!isoDate) return '';

  const [year, month, day] = isoDate.split('-');
  return year && month && day ? `${day}/${month}/${year}` : '';
}

function parseDateInput(value) {
  const match = value.trim().match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/);
  if (!match) return null;

  const [, day, month, year] = match;
  const parsed = new Date(Number(year), Number(month) - 1, Number(day));
  if (parsed.getFullYear() !== Number(year) || parsed.getMonth() !== Number(month) - 1 || parsed.getDate() !== Number(day)) {
    return null;
  }

  return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
}

function getCalendarDays(month) {
  const firstDay = new Date(month.getFullYear(), month.getMonth(), 1);
  const start = new Date(month.getFullYear(), month.getMonth(), 1 - firstDay.getDay());

  return Array.from({ length: 42 }, (_, index) => {
    const day = new Date(start);
    day.setDate(start.getDate() + index);
    return day;
  });
}

export default function PublicationSchedulePicker({ idPrefix, dateValue, timeValue, onDateChange, onTimeChange, minimumDate }) {
  const t = useTranslations('common.forms.publication_schedule');
  const [dateInputValue, setDateInputValue] = useState(formatDateInput(dateValue));
  const [timeInputValue, setTimeInputValue] = useState(timeValue || '');
  const [activePicker, setActivePicker] = useState(null);
  const [calendarMonth, setCalendarMonth] = useState(() => new Date());
  const lastDateValueRef = useRef(dateValue);
  const lastTimeValueRef = useRef(timeValue);

  useEffect(() => {
    if (dateValue !== lastDateValueRef.current) {
      setDateInputValue(formatDateInput(dateValue));
      const [year, month] = (dateValue || '').split('-').map(Number);
      setCalendarMonth(Number.isInteger(year) && Number.isInteger(month) ? new Date(year, month - 1, 1) : new Date());
      lastDateValueRef.current = dateValue;
    }

    if (timeValue !== lastTimeValueRef.current) {
      setTimeInputValue(timeValue || '');
      lastTimeValueRef.current = timeValue;
    }
  }, [dateValue, timeValue]);

  const calendarDays = useMemo(() => getCalendarDays(calendarMonth), [calendarMonth]);
  const timeOptions = useMemo(() => Array.from({ length: 96 }, (_, index) => {
    const hour = String(Math.floor(index / 4)).padStart(2, '0');
    const minute = String((index % 4) * 15).padStart(2, '0');
    return `${hour}:${minute}`;
  }), []);
  const locale = getPreferredLocale();
  const weekdays = useMemo(() => Array.from({ length: 7 }, (_, index) => {
    const date = new Date(2023, 0, 1 + index);
    return new Intl.DateTimeFormat(locale, { weekday: 'narrow' }).format(date);
  }), [locale]);

  const handleDateChange = event => {
    const value = event.target.value;
    setDateInputValue(value);
    const nextDateValue = parseDateInput(value) || '';
    lastDateValueRef.current = nextDateValue;
    onDateChange(nextDateValue);
  };

  const handleTimeChange = event => {
    const value = event.target.value;
    setTimeInputValue(value);
    const nextTimeValue = /^([01]\d|2[0-3]):[0-5]\d$/.test(value) ? value : '';
    lastTimeValueRef.current = nextTimeValue;
    onTimeChange(nextTimeValue);
  };

  const selectDate = date => {
    const isoDate = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
    setDateInputValue(formatDateInput(isoDate));
    lastDateValueRef.current = isoDate;
    onDateChange(isoDate);
    setActivePicker(null);
  };

  const selectTime = value => {
    setTimeInputValue(value);
    lastTimeValueRef.current = value;
    onTimeChange(value);
    setActivePicker(null);
  };

  return (
    <div className="publication-schedule-picker">
      <div className="publication-schedule-picker__field">
        <label className="publication-schedule-picker__label" htmlFor={`${idPrefix}-date-input`}>{t('date')}</label>
        <input
          id={`${idPrefix}-date-input`}
          type="text"
          inputMode="numeric"
          placeholder="dd/mm/yyyy"
          value={dateInputValue}
          onChange={handleDateChange}
          onFocus={() => setActivePicker('date')}
          className="publication-schedule-picker__input"
        />
        {activePicker === 'date' && (
          <div className="publication-schedule-picker__picker" role="dialog" aria-label={t('choose_date')}>
            <div className="publication-schedule-picker__picker-header">
              <button type="button" onClick={() => setCalendarMonth(month => new Date(month.getFullYear(), month.getMonth() - 1, 1))} aria-label={t('previous_month')}>‹</button>
              <strong>{calendarMonth.toLocaleDateString(locale, { month: 'long', year: 'numeric' })}</strong>
              <button type="button" onClick={() => setCalendarMonth(month => new Date(month.getFullYear(), month.getMonth() + 1, 1))} aria-label={t('next_month')}>›</button>
            </div>
            <div className="publication-schedule-picker__calendar" role="grid">
              {weekdays.map((day, index) => <span key={`${day}-${index}`}>{day}</span>)}
              {calendarDays.map(date => {
                const isoDate = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
                const isCurrentMonth = date.getMonth() === calendarMonth.getMonth();
                const isPastDate = minimumDate && isoDate < minimumDate;
                return <button key={isoDate} type="button" disabled={isPastDate || !isCurrentMonth} className={`${!isCurrentMonth ? 'outside-month ' : ''}${dateValue === isoDate ? 'selected' : ''}`} onClick={() => selectDate(date)}>{date.getDate()}</button>;
              })}
            </div>
          </div>
        )}
      </div>

      <div className="publication-schedule-picker__field">
        <label className="publication-schedule-picker__label" htmlFor={`${idPrefix}-time-input`}>{t('time')}</label>
        <input
          id={`${idPrefix}-time-input`}
          type="text"
          inputMode="numeric"
          placeholder="hh:mm"
          value={timeInputValue}
          onChange={handleTimeChange}
          onFocus={() => setActivePicker('time')}
          className="publication-schedule-picker__input"
        />
        {activePicker === 'time' && (
          <div className="publication-schedule-picker__picker publication-schedule-picker__time-picker" role="listbox" aria-label={t('choose_time')}>
            {timeOptions.map(time => <button key={time} type="button" role="option" aria-selected={timeValue === time} className={timeValue === time ? 'selected' : ''} onClick={() => selectTime(time)}>{time}</button>)}
          </div>
        )}
      </div>
    </div>
  );
}
